import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import {
  AlertCircle,
  Calendar,
  DollarSign,
  ShoppingCart,
  Store,
  Users,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { fetchStoreStats } from "../store/actions/storeActions";
import { fetchOrders } from "../store/actions/orderActions";
import { fetchSalesChart } from "../store/actions/analyticsActions";
import { formatCurrency } from "../utils/formatCurrency";
import * as ratesService from "../services/ratesService";

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  animation: fadeIn 0.5s ease-out;
`;

const H1 = styled.h1`
  margin: 0;
  font-size: 32px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const P = styled.p`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.text.body};
`;

const Grid4 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const StatCard = styled(Card)`
  padding: 24px;
`;

const StatTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StatName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.body};
`;

const StatValue = styled.div`
  margin-top: 8px;
  font-size: 30px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const IconBubble = styled.div`
  border-radius: 9999px;
  padding: 12px;
  background: rgba(155, 135, 245, 0.1);
  color: ${({ theme }) => theme.colors.primary.light};
  display: inline-flex;
`;

const statKeyNames = [
  "dashboard.totalStores",
  "dashboard.totalCustomers",
  "dashboard.totalOrders",
  "dashboard.revenue",
];
const statKeys = [
  { nameKey: statKeyNames[0], key: "totalStores", icon: Store, format: (v) => String(v ?? "0") },
  { nameKey: statKeyNames[1], key: "totalCustomers", icon: Users, format: (v) => String(v ?? "0") },
  { nameKey: statKeyNames[2], key: "activeOrders", icon: ShoppingCart, format: (v) => String(v ?? "0") },
  { nameKey: statKeyNames[3], key: "totalRevenue", icon: DollarSign, format: "currency" },
];

const alerts = [
  { id: "1", type: "warning", message: "Low inventory alert for Product XYZ" },
  { id: "2", type: "info", message: "3 new customer reviews pending approval" },
  { id: "3", type: "error", message: "Payment gateway error detected" },
];

const upcomingTasks = [
  { id: "1", title: "Review inventory levels", due: "Today", priority: "high" },
  { id: "2", title: "Approve new product listings", due: "Tomorrow", priority: "medium" },
  { id: "3", title: "Customer feedback review", due: "In 2 days", priority: "low" },
];

const Panel = styled(Card)`
  padding: 24px;
`;

const PanelTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
`;

const Chip = styled.span`
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  text-transform: capitalize;
`;

function statusChip(theme, status) {
  const c = theme.colors.status;
  if (status === "completed") return { bg: c.greenBg, fg: c.greenText };
  if (status === "pending") return { bg: c.yellowBg, fg: c.yellowText };
  if (status === "processing") return { bg: c.blueBg, fg: c.blueText };
  return { bg: c.grayBg, fg: c.grayText };
}

function priorityChip(theme, priority) {
  const c = theme.colors.status;
  if (priority === "high") return { bg: c.redBg, fg: c.redText };
  if (priority === "medium") return { bg: c.yellowBg, fg: c.yellowText };
  if (priority === "low") return { bg: c.greenBg, fg: c.greenText };
  return { bg: c.grayBg, fg: c.grayText };
}

export default function Index() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth?.user);
  const { stats: storeStats, loading: storeLoading } = useSelector((state) => state.store);
  const { orders = [], loading: ordersLoading } = useSelector((state) => state.order);
  const { salesChart = [], loading: chartLoading } = useSelector((state) => state.analytics);
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [rates, setRates] = useState(null);

  useEffect(() => {
    ratesService.getRates().then((r) => setRates(r?.rates || null)).catch(() => setRates(null));
  }, []);

  useEffect(() => {
    dispatch(fetchStoreStats());
    dispatch(fetchOrders());
    dispatch(fetchSalesChart(30));
  }, [dispatch]);

  const baseCurrency = "USD";
  const statData = statKeys.map((s) => {
    const raw = storeStats?.[s.key];
    let value = storeLoading ? "…" : raw == null ? "—" : String(raw);
    if (s.format === "currency" && typeof raw === "number") {
      const converted = rates ? ratesService.convert(raw, baseCurrency, currency, rates) : raw;
      value = formatCurrency(converted, currency);
    } else if (s.format && typeof s.format === "function") {
      value = storeStats ? s.format(storeStats[s.key]) : value;
    }
    return { ...s, value };
  });

  const revenueData = (salesChart || []).map((d) => {
    const v = d.total ?? 0;
    const converted = rates ? ratesService.convert(v, baseCurrency, currency, rates) : v;
    return { name: d.date, value: converted };
  }).slice(-14);
  const recentOrdersList = (orders || []).slice(0, 5);

  return (
    <Page className="animate-fadeIn">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <H1>{t("dashboard.title")}</H1>
          <P>{t("dashboard.welcomeBack")}</P>
        </div>
        <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="GBP">GBP</option>
        </select>
      </div>

      <Grid4>
        {statData.map((s) => {
          const Icon = s.icon;
          return (
            <StatCard key={s.nameKey} className="glass-card glass-card-hover">
              <StatTop>
                <div>
                  <StatName>{t(s.nameKey)}</StatName>
                  <StatValue>{s.value}</StatValue>
                </div>
                <IconBubble>
                  <Icon size={24} />
                </IconBubble>
              </StatTop>
            </StatCard>
          );
        })}
      </Grid4>

      <TwoCol>
        <Panel className="glass-card">
          <PanelTitle>{t("dashboard.revenueOverview")}</PanelTitle>
          <div style={{ height: 300 }}>
            {chartLoading && revenueData.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#6b7280" }}>{t("common.loading")}</div>
            ) : revenueData.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#6b7280" }}>{t("dashboard.syncOrdersHint")}</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(v) => [formatCurrency(Number(v), currency), t("dashboard.revenue")]} />
                  <Line type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={2} name={t("dashboard.revenue")} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Panel>

        <Panel className="glass-card">
          <PanelTitle>{t("dashboard.recentOrders")}</PanelTitle>
          <List>
            {ordersLoading && recentOrdersList.length === 0 ? (
              <div style={{ padding: 24, color: "#6b7280" }}>{t("common.loading")}</div>
            ) : recentOrdersList.length === 0 ? (
              <div style={{ padding: 24, color: "#6b7280" }}>{t("dashboard.noOrdersYet")}</div>
            ) : (
              recentOrdersList.map((o) => (
                <Row key={o._id}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{o.customerId?.name || o.customerId?.email || `Order #${o.orderId}`}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{o.date ? new Date(o.date).toLocaleDateString() : "—"}</div>
                  </div>
                  <OrderRight status={o.status} amount={typeof o.total === "number" ? (rates && (o.currency || "USD") !== currency ? formatCurrency(ratesService.convert(o.total, o.currency || "USD", currency, rates), currency) : formatCurrency(o.total, o.currency || "USD")) : "—"} />
                </Row>
              ))
            )}
            <Button $variant="outline" onClick={() => navigate("/orders")}>{t("dashboard.viewAllOrders")}</Button>
          </List>
        </Panel>
      </TwoCol>

      <TwoCol>
        <Panel className="glass-card">
          <PanelTitle>{t("dashboard.alertsAndNotifications")}</PanelTitle>
          <List>
            {alerts.map((a) => (
              <Row key={a.id} style={{ alignItems: "flex-start" }}>
                <AlertIcon type={a.type} />
                <div style={{ fontSize: 14, color: "#4b5563" }}>{a.message}</div>
              </Row>
            ))}
          </List>
        </Panel>

        <Panel className="glass-card">
          <PanelTitle>{t("dashboard.upcomingTasks")}</PanelTitle>
          <List>
            {upcomingTasks.map((task) => (
              <TaskRow key={task.id} task={task} />
            ))}
            <Button $variant="outline" onClick={() => navigate("/tasks")}>{t("dashboard.viewAllTasks")}</Button>
          </List>
        </Panel>
      </TwoCol>
    </Page>
  );
}

function AlertIcon({ type }) {
  const color =
    type === "error" ? "#ef4444" : type === "warning" ? "#eab308" : "#3b82f6";
  return <AlertCircle size={20} color={color} style={{ marginTop: 2 }} />;
}

function OrderRight({ status, amount }) {
  return (
    <OrderRightWrap>
      <span style={{ fontWeight: 700 }}>{amount}</span>
      <StatusChip status={status} />
    </OrderRightWrap>
  );
}

const OrderRightWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatusChip = styled(Chip)`
  ${({ theme, status }) => {
    const { bg, fg } = statusChip(theme, status);
    return `background:${bg}; color:${fg};`;
  }}
`;

function TaskRow({ task }) {
  return (
    <Row>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Calendar size={18} color="#6b7280" />
        <div>
          <div style={{ fontWeight: 700 }}>{task.title}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Due: {task.due}</div>
        </div>
      </div>
      <PriorityChip priority={task.priority} />
    </Row>
  );
}

const PriorityChip = styled(Chip)`
  ${({ theme, priority }) => {
    const { bg, fg } = priorityChip(theme, priority);
    return `background:${bg}; color:${fg};`;
  }}
`;

